/**
 * The project chat (docs/collaboration.md «Project chat»): the round «Чат» button beside «Поделиться» with the count
 * of unread messages, and, open, a column at the right of the window — the stage narrows beside it as it does for the
 * agent's column at the left. Messages come in groups by author and day, the others' at the left under their
 * avatar, yours at the right with a clock, one tick or two; everyone's avatar stands under the last message they
 * have read; a line under the thread says who is typing. Hover, focus or a tap shows a message's reactions, the
 * answer and the menu: copy, and for your own, edit and delete.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { projectChat, type ChatDraft, type ProjectChatState } from '../../collaboration/chat/controller';
import type { ChatSession, ChatSnapshot } from '../../collaboration/chat/session';
import { CHAT_QUICK_REACTIONS, CHAT_REACTIONS, CHAT_TEXT_LIMIT, type ChatReaction } from '../../collaboration/chat/protocol';
import { firstUnread, ownStatus, reactionSummary, readHeads, readersOf, typingUsers, unreadCount, type ChatMessage, type ChatState } from '../../collaboration/chat/state';
import { demoBots } from '../../collaboration/demoBots';
import type { ParticipantLook } from '../../collaboration/presenceSource';
import { agentSession } from '../../agent/runtime';
import { ParticipantAvatar } from './CollaborationOverlay';
import { plainLook } from '../../collaboration/participantColors';
import { ActionMenu, type MenuItem } from '../Dropdown';
import { HomeDialog } from '../home/HomeDialog';
import { Icon } from '../Icon';
import { usePresence } from '../usePresence';
import { cultureName, t, type TranslationKey } from '../../i18n/I18n';

/** Below this width two columns leave the stage too little room: opening one chat closes the other. */
const NARROW = '(max-width: 1679px)';
/** Messages of one author closer than this share one avatar and one name. */
const GROUP_GAP_MS = 5 * 60_000;

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');
const lookFor = (userId: string): ParticipantLook => projectChat.look(userId) ?? plainLook(userId);
/** The stripe of a quote: the quoted author's colour, the chrome's blue for your own words. */
const quoteColor = (userId: string, selfId: string): string => userId === selfId ? 'var(--chrome-blue)' : lookFor(userId).color;
const nameIn = (state: ChatState, userId: string, selfId: string): string => userId === selfId ? t('#ChatYou') : state.names[userId] ?? t('#ChatParticipant');
const timeLabel = (at: number): string => new Date(at).toLocaleTimeString(cultureName(), { hour: '2-digit', minute: '2-digit' });
const fullTime = (at: number): string => new Date(at).toLocaleString(cultureName(), { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const dayStart = (at: number): number => { const date = new Date(at); return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(); };
/** «Сегодня», «Вчера», «12 сентября», a year only when it is not this one. */
function dayLabel(at: number, now = Date.now()): string {
  const days = Math.round((dayStart(now) - dayStart(at)) / 86_400_000);
  if (days === 0) return t('#Today');
  if (days === 1) return t('#Yesterday');
  const sameYear = new Date(at).getFullYear() === new Date(now).getFullYear();
  return new Date(at).toLocaleDateString(cultureName(), sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' });
}
/** «Mark», «Mark и Mason», «Mark, Mason и Olga», «Mark, Mason и ещё 2». */
export function namesLine(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return t('#ChatNamesTwo', names[0], names[1]);
  if (names.length === 3) return t('#ChatNamesThree', names[0], names[1], names[2]);
  return t('#ChatNamesMore', names[0], names[1], names.length - 2);
}
/** «Mark печатает…», «Mark и Mason печатают…». */
export const typingLine = (names: readonly string[]): string => names.length ? t(names.length === 1 ? '#ChatTypingSingle' : '#ChatTypingSeveral', namesLine(names)) : '';
const excerpt = (text: string, max = 90): string => { const line = text.replace(/\s+/g, ' ').trim(); return line.length > max ? line.slice(0, max - 1).replace(/\s+\S*$/, '') + '…' : line; };
/** One to three emoji and nothing else: drawn large, without a bubble's worth of text around them. */
const emojiOnly = (text: string): boolean => {
  const pictures = text.match(/\p{Extended_Pictographic}/gu)?.length ?? 0;
  return pictures > 0 && pictures <= 3 && /^[\p{Extended_Pictographic}\p{Emoji_Component}\u200d\ufe0f\s]+$/u.test(text);
};
/** Web addresses become links that open in a new tab; everything else stays plain text. */
function linkify(text: string): ReactNode[] {
  return text.split(/(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/g).map((piece, index) => index % 2
    ? <a key={index} href={piece} target="_blank" rel="noopener noreferrer">{piece}</a> : piece);
}

function useChat(): ProjectChatState { return useSyncExternalStore(projectChat.subscribe, projectChat.get, projectChat.get); }
function useSession(session: ChatSession | null): ChatSnapshot | null {
  const subscribe = useCallback((listener: () => void) => session ? session.subscribe(listener) : () => {}, [session]);
  return useSyncExternalStore(subscribe, () => session?.get() ?? null, () => null);
}
function useUnread(session: ChatSession | null): number {
  const subscribe = useCallback((listener: () => void) => session ? session.subscribe(listener) : () => {}, [session]);
  return useSyncExternalStore(subscribe, () => session ? unreadCount(session.get().state, session.self.userId) : 0, () => 0);
}

/** Opening the chat where two columns do not fit closes the agent's. */
function openChat(open: boolean): void {
  if (open && matchMedia(NARROW).matches && agentSession().get().open) agentSession().setOpen(false);
  projectChat.setOpen(open);
}

/** A participant card's «Message»: the chat opens with the composer addressed to them. */
export function messageInChat(name: string): void {
  openChat(true);
  projectChat.address(name);
}

/** The header's round «Чат» beside «Поделиться»: the count of unread messages on it, pressed while the column is open. */
export function ChatButton() {
  const chat = useChat();
  const unread = useUnread(chat.session);
  const count = unread > 99 ? '99+' : String(unread);
  return <button type="button" className="chat-button" aria-expanded={chat.open} aria-controls="project-chat"
    aria-label={unread ? t('#ChatProjectChatUnread', unread) : t('#ChatProjectChat')} data-tip={t('#ChatProjectChat')} onClick={() => openChat(!chat.open)}>
    <Icon name="chat" size={20} />
    {unread > 0 && <span className="chat-badge" aria-hidden="true">{count}</span>}
  </button>;
}

/** The chat as the people's list «+N» offers it while the round button has stepped aside for them: the unread count and the way in. */
export function useChatEntry(): { unread: number; open: boolean; onOpen: () => void } {
  // Only the session, its count and whether the column is open: the editor that holds the list does not redraw for every
  // member or message.
  const session = useSyncExternalStore(projectChat.subscribe, () => projectChat.get().session, () => null);
  const open = useSyncExternalStore(projectChat.subscribe, projectChat.isOpen, () => false);
  const unread = useUnread(session);
  return useMemo(() => ({ unread, open, onOpen: () => openChat(true) }), [unread, open]);
}

/** Always mounted beside the stage: the column while the chat is open, with its exit while it closes. */
export function ChatDock({ onInvite }: { onInvite: () => void }) {
  const chat = useChat();
  const agentOpen = useSyncExternalStore(agentSession().subscribe, () => agentSession().get().open, () => false);
  const { mounted, closing } = usePresence(chat.open, 160);
  const agentWas = useRef(agentOpen);
  // The agent's column, opened where two do not fit, takes the place.
  useEffect(() => {
    if (agentOpen && !agentWas.current && projectChat.isOpen() && matchMedia(NARROW).matches) projectChat.setOpen(false);
    agentWas.current = agentOpen;
  }, [agentOpen]);
  return mounted ? <ChatColumn chat={chat} closing={closing} onInvite={onInvite} /> : null;
}

const sessionKeys = new WeakMap<ChatSession, number>();
let nextSessionKey = 1;
const keyOf = (session: ChatSession): number => { let key = sessionKeys.get(session); if (!key) sessionKeys.set(session, key = nextSessionKey++); return key; };

function ChatColumn({ chat, closing, onInvite }: { chat: ProjectChatState; closing: boolean; onInvite: () => void }) {
  const root = useRef<HTMLElement>(null);
  const close = useCallback(() => {
    projectChat.setOpen(false);
    // Back to the button that opens the chat: the round one, or the people's «+N» where it has given them its place.
    const button = document.querySelector<HTMLElement>('.chat-button');
    (button?.offsetWidth ? button : document.querySelector<HTMLElement>('.collab-more'))?.focus({ preventScroll: true });
  }, []);
  const others = chat.members.map(member => member.name);
  const subtitle = chat.availability === 'unavailable' ? t('#ChatWaitingForServer') : others.length ? t('#ChatYouAnd', others.join(', ')) : t('#ChatOnlyYou');
  // Open with the field ready to type into, unless it opened under a finger; without a field, the column itself.
  // Later, when the chat gets its participants, the field takes the focus only from the column itself.
  const opened = useRef(false);
  useEffect(() => {
    if (closing) return;
    const active = document.activeElement, first = !opened.current;
    opened.current = true;
    if (!first && active && active !== document.body && !root.current?.contains(active)) return;
    const field = root.current?.querySelector<HTMLTextAreaElement>('.chat-input textarea');
    if (field && !matchMedia('(pointer: coarse)').matches) field.focus({ preventScroll: true });
    else if (!root.current?.contains(active)) root.current?.focus({ preventScroll: true });
  }, [closing, chat.availability]);
  return <section ref={root} id="project-chat" className={cx('chat-dock', closing && 'is-closing')} aria-label={t('#ChatProjectChat')} inert={closing} tabIndex={-1}
    onKeyDown={event => {
      if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); close(); }
      // Keys pressed in the chat are the chat's: Delete on a message must not delete what is selected on the stage.
      event.stopPropagation();
    }}>
    <header className="chat-head">
      <div className="chat-head-text"><h2>{t('#ChatProjectChat')}</h2><p title={subtitle}>{subtitle}</p></div>
      {chat.members.length > 0 && <span className="chat-head-people" aria-hidden="true">
        {chat.members.slice(0, 3).map(member => <ParticipantAvatar key={member.id} name={member.name} look={lookFor(member.id)} size={24} />)}
      </span>}
      <button type="button" className="chat-mini" aria-label={t('#ChatCloseChat')} title={t('#ChatCloseEsc')} onClick={close}><Icon name="close" size={20} /></button>
    </header>
    {chat.session ? <Conversation key={keyOf(chat.session)} session={chat.session} projectId={chat.projectId} closing={closing} onClose={close} />
      : chat.availability === 'unavailable' ? <ChatUnavailable /> : <ChatIntro onInvite={onInvite} />}
  </section>;
}

/** Nobody else in the project: what the chat is for, the way in through «Поделиться», and the demo to see it work. */
function ChatIntro({ onInvite }: { onInvite: () => void }) {
  return <div className="chat-intro">
    <span className="chat-intro-icon" aria-hidden="true"><Icon name="chat" size={26} /></span>
    <h3>{t('#ChatIntroTitle')}</h3>
    <p>{t('#ChatIntroText')}</p>
    <p className="chat-fineprint">{t('#ChatIntroFineprint')}</p>
    <div className="chat-intro-actions">
      <button type="button" className="chat-action is-primary" onClick={() => demoBots.set(true)}><Icon name="multiplayer" size={18} />{t('#ChatShowExample')}</button>
      <button type="button" className="chat-action" onClick={onInvite}>{t('Share')}</button>
    </div>
  </div>;
}

function ChatUnavailable() {
  return <div className="chat-intro">
    <span className="chat-intro-icon" aria-hidden="true"><Icon name="chat" size={26} /></span>
    <h3>{t('#ChatUnavailableTitle')}</h3>
    <p>{t('#ChatUnavailableText')}</p>
  </div>;
}

/** The chat of one session: the banners, the thread, who is typing, the composer, and the question before a deletion. */
function Conversation({ session, projectId, closing, onClose }: { session: ChatSession; projectId: string | null; closing: boolean; onClose: () => void }) {
  const snapshot = useSession(session)!;
  const { state, self } = snapshot;
  const [draft, setDraftState] = useState<ChatDraft>(() => projectChat.draft(projectId));
  const setDraft = useCallback((update: ChatDraft | ((draft: ChatDraft) => ChatDraft)) => setDraftState(current => {
    const next = typeof update === 'function' ? update(current) : update;
    projectChat.setDraft(projectId, next);
    return next;
  }), [projectId]);
  const composer = useRef<HTMLTextAreaElement>(null);
  /** The draft set aside while you edit an older message, back when the edit ends. */
  const stash = useRef('');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = (text: string) => {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 2200);
  };
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); session.setTyping(false); }, [session]);
  // A screen reader hears what the others write while the column is open; the history it can read at its pace.
  const [incoming, setIncoming] = useState('');
  const heard = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const last = state.messages.at(-1);
    if (!snapshot.ready) return;
    const known = heard.current;
    heard.current = last?.id ?? null;
    if (known === undefined || !last || last.id === known || last.authorId === self.userId || last.deleted) return;
    setIncoming(`${nameIn(state, last.authorId, self.userId)}: ${excerpt(last.text, 160)}`);
  }, [state, snapshot.ready, self.userId]);
  const focusComposer = () => requestAnimationFrame(() => composer.current?.focus({ preventScroll: true }));
  const addressed = useSyncExternalStore(projectChat.subscribe, () => projectChat.get().addressed);
  const addressedSeen = useRef(addressed);
  useEffect(() => {
    if (addressed === addressedSeen.current) return;
    addressedSeen.current = addressed;
    setDraftState(projectChat.draft(projectId)); focusComposer();
  }, [addressed, projectId]);

  // An answer to, or an edit of, a message that has just been deleted ends with it.
  useEffect(() => {
    const alive = (id: string | null) => !id || state.messages.some(message => message.id === id && !message.deleted);
    if (!alive(draft.editing)) { setDraft({ text: stash.current, replyTo: null, editing: null }); stash.current = ''; }
    else if (!alive(draft.replyTo)) setDraft(current => ({ ...current, replyTo: null }));
  }, [state.messages, draft.editing, draft.replyTo, setDraft]);

  const reply = (id: string) => { setDraft(current => ({ ...current, replyTo: id, ...(current.editing ? { editing: null, text: stash.current } : {}) })); focusComposer(); };
  const edit = (id: string) => {
    const message = state.messages.find(candidate => candidate.id === id);
    if (!message) return;
    if (!draft.editing) stash.current = draft.text;
    setDraft({ text: message.text, replyTo: null, editing: id });
    session.setTyping(false);
    focusComposer();
  };
  const cancelContext = () => {
    if (draft.editing) { setDraft({ text: stash.current, replyTo: null, editing: null }); stash.current = ''; }
    else setDraft(current => ({ ...current, replyTo: null }));
  };
  const editLast = (): boolean => {
    const own = [...state.messages].reverse().find(message => message.authorId === self.userId && !message.deleted && !message.local);
    if (!own) return false;
    edit(own.id);
    return true;
  };
  const submit = () => {
    if (draft.editing) {
      session.edit(draft.editing, draft.text);
      setDraft({ text: stash.current, replyTo: null, editing: null }); stash.current = '';
      return;
    }
    if (session.send(draft.text, draft.replyTo)) setDraft({ text: '', replyTo: null, editing: null });
  };
  const copy = async (message: ChatMessage) => {
    try { await navigator.clipboard.writeText(message.text); flash(t('#TextCopied')); }
    catch { flash(t('#ChatCopyFailed')); }
  };

  return <>
    {snapshot.connection === 'disconnected' && <div className="chat-banner" role="status">
      <Icon name="info" size={16} /><span>{snapshot.ready ? t('#ChatDisconnected') : t('#ChatConnectFailed')}</span>
      <button type="button" className="chat-link" onClick={() => session.reconnect()}>{t('#ChatRetry')}</button>
    </div>}
    {snapshot.connection === 'connecting' && snapshot.ready && <div className="chat-banner is-quiet" role="status"><span className="chat-spinner" aria-hidden="true" /><span>{t('#ChatConnecting')}</span></div>}
    {snapshot.error && snapshot.connection !== 'disconnected' && <div className="chat-banner is-error" role="alert">
      <span>{t(snapshot.error)}</span>
      <button type="button" className="chat-mini is-small" aria-label={t('#ChatDismissNotice')} onClick={() => session.dismissError()}><Icon name="close" size={14} /></button>
    </div>}
    <Thread session={session} snapshot={snapshot} closing={closing} composer={composer}
      onReply={reply} onEdit={edit} onDelete={setConfirm} onCopy={message => void copy(message)} />
    <TypingLine state={state} selfId={self.userId} />
    <Composer session={session} snapshot={snapshot} draft={draft} setDraft={setDraft} textarea={composer}
      onSubmit={submit} onCancelContext={cancelContext} onEditLast={editLast} onClose={onClose} />
    <div className="chat-sr" role="status" aria-live="polite">{notice}</div>
    <div className="chat-sr" aria-live="polite" aria-atomic="true">{incoming}</div>
    {notice && <div className="chat-toast" aria-hidden="true">{notice}</div>}
    {confirm && <HomeDialog title={t('#ChatDeleteTitle')} onClose={() => setConfirm(null)}>{dismiss => <>
      <p>{t('#ChatDeleteText')}</p>
      <div className="dialog-actions">
        <button type="button" className="ws-button" onClick={() => dismiss()}>{t('Cancel')}</button>
        <button type="button" className="ws-button danger" onClick={() => dismiss(() => { session.remove(confirm); focusComposer(); })}>{t('Delete')}</button>
      </div>
    </>}</HomeDialog>}
  </>;
}

type Row = { message: ChatMessage; day: boolean; divider: boolean; grouped: boolean; groupedNext: boolean };

/**
 * The messages: the day over each day's first, «Новые сообщения» where the unread began when the chat opened, a
 * button back down while you read higher up. What stays in view long enough, in a visible and focused window, is
 * read; the relay learns the last of it.
 */
function Thread({ session, snapshot, closing, composer, onReply, onEdit, onDelete, onCopy }: {
  session: ChatSession; snapshot: ChatSnapshot; closing: boolean; composer: RefObject<HTMLTextAreaElement | null>;
  onReply: (id: string) => void; onEdit: (id: string) => void; onDelete: (id: string) => void; onCopy: (message: ChatMessage) => void;
}) {
  const { state, self, ready } = snapshot;
  const selfId = self.userId;
  const list = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [touched, setTouched] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);
  const pointer = useRef('mouse');
  // Where the unread began when the history first arrived: the line stays there while they get read.
  const [divider, setDivider] = useState<string | null>(() => ready ? firstUnread(state, selfId) : null);
  const dividerFixed = useRef(ready);
  if (ready && !dividerFixed.current) { dividerFixed.current = true; const first = firstUnread(state, selfId); if (first !== divider) setDivider(first); }
  const unread = unreadCount(state, selfId);
  const heads = useMemo(() => readHeads(state, selfId), [state, selfId]);
  const messages = state.messages;
  const last = messages.at(-1);

  const rows = useMemo(() => {
    const result: Row[] = [];
    messages.forEach((message, index) => {
      const previous = messages[index - 1];
      const day = !previous || dayStart(previous.sentAt) !== dayStart(message.sentAt);
      const isDivider = message.id === divider;
      const grouped = !!previous && !day && !isDivider && previous.authorId === message.authorId && message.sentAt - previous.sentAt < GROUP_GAP_MS;
      if (grouped) result[result.length - 1].groupedNext = true;
      result.push({ message, day, divider: isDivider, grouped, groupedNext: false });
    });
    return result;
  }, [messages, divider]);

  const measure = useCallback(() => {
    const element = list.current;
    if (!element) return;
    const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
    atBottom.current = bottom;
    setShowJump(!bottom);
  }, []);
  const toBottom = useCallback((smooth = false) => {
    const element = list.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
    atBottom.current = true; setShowJump(false);
  }, []);

  // The first view: at the «Новые сообщения» line when there is one, else at the end.
  const placed = useRef(false);
  useLayoutEffect(() => {
    const element = list.current;
    if (!ready || placed.current || !element) return;
    placed.current = true;
    const line = element.querySelector<HTMLElement>('.chat-divider');
    if (line) element.scrollTop = Math.max(0, line.offsetTop - 16); else element.scrollTop = element.scrollHeight;
    measure();
  }, [ready, measure]);
  // A new message keeps the end in view when you were there, and always when it is yours.
  const lastMine = last?.authorId === selfId;
  useLayoutEffect(() => {
    if (!placed.current) return;
    if (lastMine) { setDivider(null); toBottom(); } else if (atBottom.current) toBottom();
  }, [last?.id, lastMine, toBottom]);
  // Reactions, answers and fonts change heights under the end too.
  useEffect(() => {
    const element = content.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      const element = list.current;
      if (element && atBottom.current && placed.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ready]);

  // Read what is seen: half a message in view, the tab visible and the window focused.
  useEffect(() => {
    const root = list.current;
    if (!root || closing || !ready) return;
    const visible = new Set<string>();
    const flush = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      const order = new Map(session.get().state.messages.map((message, index) => [message.id, index]));
      let best: string | null = null, at = -1;
      for (const id of visible) { const index = order.get(id) ?? -1; if (index > at) { at = index; best = id; } }
      if (best) session.markRead(best);
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).dataset.readTarget;
        if (!id) continue;
        const seen = entry.isIntersecting && (entry.intersectionRatio >= .5 || entry.intersectionRect.height >= root.clientHeight * .5);
        if (seen) visible.add(id); else visible.delete(id);
      }
      flush();
    }, { root, threshold: [0, .5, 1] });
    const observeAll = () => root.querySelectorAll<HTMLElement>('[data-read-target]').forEach(element => observer.observe(element));
    observeAll();
    const added = new MutationObserver(observeAll);
    added.observe(root, { childList: true, subtree: true });
    window.addEventListener('focus', flush);
    document.addEventListener('visibilitychange', flush);
    return () => { observer.disconnect(); added.disconnect(); window.removeEventListener('focus', flush); document.removeEventListener('visibilitychange', flush); };
  }, [session, closing, ready]);

  const jumpTo = (id: string) => {
    const target = list.current?.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(id)}"]`);
    if (!target) return;
    target.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    target.focus({ preventScroll: true });
    setHighlight(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlight(null), 1400);
  };

  // The messages take the focus in turn: ↑ ↓ Home End walk them, Tab goes into the one in focus, Escape back to the field.
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('chat-message')) {
      // Escape from a message's own buttons returns to the message.
      const message = target.closest<HTMLElement>('.chat-message');
      if (event.key === 'Escape' && message && !event.defaultPrevented) { event.preventDefault(); message.focus(); }
      return;
    }
    const items = [...(list.current?.querySelectorAll<HTMLElement>('.chat-message') ?? [])];
    const index = items.indexOf(target);
    let next: HTMLElement | undefined;
    if (event.key === 'ArrowUp') next = items[index - 1];
    else if (event.key === 'ArrowDown') next = items[index + 1];
    else if (event.key === 'Home') next = items[0];
    else if (event.key === 'End') next = items.at(-1);
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); composer.current?.focus(); return; }
    else return;
    event.preventDefault();
    next?.focus();
  };

  const focusable = active && messages.some(message => message.id === active) ? active : last?.id ?? null;
  return <div className="chat-thread-wrap">
    <div ref={list} className="chat-thread" role="log" aria-label={t('#ChatMessage')} aria-live="off" aria-busy={!ready} onScroll={measure} onKeyDown={keyDown}
      onPointerDown={event => { pointer.current = event.pointerType; if (!(event.target as HTMLElement).closest('.chat-message')) setTouched(null); }}>
      <div ref={content} className="chat-thread-content">
        {!ready && snapshot.connection === 'disconnected' ? <div className="chat-empty">
          <p><strong>{t('#ChatNotLoadedTitle')}</strong></p>
          <p>{t('#ChatNotLoadedText')}</p>
        </div> : !ready ? <ThreadSkeleton /> : !messages.length ? <div className="chat-empty">
          <p><strong>{t('#ChatEmptyTitle')}</strong></p>
          <p>{t('#ChatEmptyText')}</p>
        </div> : rows.map(row => <MessageRow key={row.message.id} row={row} state={state} selfId={selfId} heads={heads.get(row.message.id)}
          focusable={row.message.id === focusable} touched={touched === row.message.id} highlighted={highlight === row.message.id}
          onFocus={setActive} onTap={(id, show) => {
            if (show) setTouched(id);
            else if (pointer.current !== 'mouse' && pointer.current !== 'pen') setTouched(current => current === id ? null : id);
          }}
          onReply={onReply} onEdit={onEdit} onDelete={onDelete} onCopy={onCopy} onJump={jumpTo}
          onReact={(id, emoji) => session.react(id, emoji)} onRetry={id => session.retry(id)} onDiscard={id => session.discard(id)} />)}
      </div>
    </div>
    {ready && showJump && <button type="button" className="chat-jump" onClick={() => toBottom(true)}
      aria-label={unread ? t('#ChatJumpToNew', unread) : t('#ChatJumpToLast')} title={unread ? t('#ChatNewCount', unread) : t('#ChatJumpDown')}>
      <Icon name="chevronDown" size={18} />{unread > 0 && <span className="chat-jump-count">{unread > 99 ? '99+' : unread}</span>}
    </button>}
  </div>;
}

function ThreadSkeleton() {
  return <div className="chat-skeleton">
    {[{ mine: false, width: 190 }, { mine: false, width: 132 }, { mine: true, width: 170 }, { mine: false, width: 220 }].map((row, index) =>
      <div key={index} className={cx('chat-skeleton-row', row.mine && 'is-mine')} aria-hidden="true">{!row.mine && <i className="chat-skeleton-avatar" />}<i className="chat-skeleton-bubble" style={{ width: row.width }} /></div>)}
    <span className="chat-sr">{t('#ChatLoading')}</span>
  </div>;
}

/** The ticks of your message: a clock on its way, one when the relay has it, two once someone has read it. */
function Ticks({ status, readers }: { status: ReturnType<typeof ownStatus>; readers: string[] }) {
  const label = status === 'pending' ? t('#ChatSending') : status === 'failed' ? t('#ChatNotSent') : status === 'read' ? t('#ChatReadBy', namesLine(readers)) : t('#ChatDelivered');
  return <span className={`chat-ticks is-${status}`} role="img" aria-label={label} title={label}>
    {status === 'pending' ? <Icon name="clock" size={13} /> : status === 'failed' ? <Icon name="info" size={13} />
      : <svg width="17" height="11" viewBox="0 0 17 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m1 5.8 3.4 3.4L11.2 2.4" />{status === 'read' && <path d="m7.5 8.3.9.9L15.2 2.4" />}
      </svg>}
  </span>;
}

/** What the picker and a screen reader call each reaction; typing in the open picker finds them by these names. */
const REACTION_NAMES: Record<ChatReaction, TranslationKey> = {
  '❤️': '#ChatReactionHeart', '😂': '#ChatReactionLaugh', '😮': '#ChatReactionSurprise', '😢': '#ChatReactionSad', '😡': '#ChatReactionAngry', '👍': '#ChatReactionLike',
  '🔥': '#ChatReactionFire', '👏': '#ChatReactionApplause', '🎉': '#ChatReactionCelebration', '🙏': '#ChatReactionThanks', '😍': '#ChatReactionAdore', '🥰': '#ChatReactionAffection', '😊': '#ChatReactionSmile',
  '😅': '#ChatReactionAwkward', '🤔': '#ChatReactionHmm', '👀': '#ChatReactionLooking', '✅': 'Done', '❌': 'No', '💯': '#ChatReactionHundred', '🙌': '#ChatReactionHooray', '😎': '#ChatReactionCool',
  '🤩': '#ChatReactionStarStruck', '🥳': '#ChatReactionParty', '😬': '#ChatReactionOops', '🤝': '#ChatReactionDeal', '💪': '#ChatReactionStrong', '✨': '#ChatReactionSparkle', '🏠': '#ChatReactionHome',
  '🛋️': '#ChatReactionSofa', '🎨': '#ChatReactionPaint',
};
const reactionName = (emoji: ChatReaction): string => t(REACTION_NAMES[emoji]);
/** The picker is seven cells wide: the six quick reactions and «+», then every reaction in rows of seven. */
const PICKER_COLUMNS = 7;
/** A finger held this long on a message opens its reactions, as in Instagram. */
const LONG_PRESS_MS = 450;

/**
 * 😊 beside a message: Instagram's quick reactions in a row with «+» for the rest, yours lit. The picker is the shared
 * menu laid out as a grid, so the arrows walk it, Enter picks and Escape returns to 😊.
 */
function ReactionPicker({ current, align, onPick }: { current: ChatReaction | null; align: 'start' | 'end'; onPick: (emoji: ChatReaction) => void }) {
  const [more, setMore] = useState(false);
  const cell = (emoji: ChatReaction): MenuItem => ({ id: emoji, label: reactionName(emoji), checked: emoji === current,
    content: <span className="chat-picker-emoji" aria-hidden="true">{emoji}</span>, onSelect: () => onPick(emoji) });
  const items: MenuItem[] = more ? CHAT_REACTIONS.map(cell)
    : [...CHAT_QUICK_REACTIONS.map(cell), { id: 'more', label: t('#ChatAllReactions'), keepOpen: true, content: <Icon name="plus" size={18} />, onSelect: () => setMore(true) }];
  return <ActionMenu label={current ? t('#ChatReactionChange', reactionName(current)) : t('#ChatReact')} columns={PICKER_COLUMNS} width={PICKER_COLUMNS * 42 + 12}
    align={align} contain=".chat-dock" className="chat-picker" triggerClassName="chat-icon-button" trigger={<Icon name="reaction" size={20} />}
    onOpenChange={open => { if (!open) setMore(false); }} items={items} />;
}

/**
 * The reactions on a message's bottom corner, as in Instagram: up to three kinds, the most chosen first, and how many
 * reacted when more than one did. It opens who reacted with what; your own row takes yours off.
 */
function ReactionsPill({ state, message, selfId, onRemove }: { state: ChatState; message: ChatMessage; selfId: string; onRemove: () => void }) {
  const summary = reactionSummary(state, message.id);
  if (!summary) return null;
  const people = summary.people.map(([user, emoji]) => ({ user, emoji, name: nameIn(state, user, selfId) }));
  const items: MenuItem[] = people.map(({ user, emoji, name }) => ({
    id: user, label: user === selfId ? t('#ChatReactorOwn', name, reactionName(emoji)) : `${name}: ${reactionName(emoji)}`,
    icon: <ParticipantAvatar name={name} look={lookFor(user)} size={28} />,
    content: <span className="chat-reactor"><span className="chat-reactor-name"><strong>{name}</strong>{user === selfId && <small>{t('#ChatClickToRemove')}</small>}</span>
      <span className="chat-reactor-emoji" aria-hidden="true">{emoji}</span></span>,
    onSelect: user === selfId ? onRemove : undefined,
  }));
  return <ActionMenu label={t('#ChatReactionsList', people.map(person => `${person.name} ${reactionName(person.emoji)}`).join(', '))} heading={t('Reactions')} width={260}
    align="start" contain=".chat-dock" className="chat-reactions-anchor" triggerClassName="chat-reaction-pill" items={items}
    trigger={<>{summary.top.map(emoji => <span key={emoji} className="chat-pill-emoji" aria-hidden="true">{emoji}</span>)}
      {summary.total > 1 && <span className="chat-pill-count" aria-hidden="true">{summary.total}</span>}</>} />;
}

function MessageRow({ row, state, selfId, heads, focusable, touched, highlighted, onFocus, onTap, onReply, onEdit, onDelete, onCopy, onJump, onReact, onRetry, onDiscard }: {
  row: Row; state: ChatState; selfId: string; heads: string[] | undefined; focusable: boolean; touched: boolean; highlighted: boolean;
  onFocus: (id: string) => void; onTap: (id: string, show?: boolean) => void; onReply: (id: string) => void; onEdit: (id: string) => void; onDelete: (id: string) => void;
  onCopy: (message: ChatMessage) => void; onJump: (id: string) => void; onReact: (id: string, emoji: ChatReaction | null) => void;
  onRetry: (id: string) => void; onDiscard: (id: string) => void;
}) {
  const { message, grouped, groupedNext } = row;
  const mine = message.authorId === selfId;
  const name = nameIn(state, message.authorId, selfId);
  const look = lookFor(message.authorId);
  const myReaction = state.reactions[message.id]?.[selfId] ?? null;
  const reacted = !message.deleted && !!state.reactions[message.id];
  const replied = message.replyTo ? state.messages.find(candidate => candidate.id === message.replyTo) : undefined;
  const status = mine ? ownStatus(state, message) : null;
  const readers = mine ? readersOf(state, message).map(user => nameIn(state, user, selfId)) : [];
  const tools = !message.deleted && !message.local;
  const labelId = `chat-label-${message.id}`;
  const article = useRef<HTMLElement>(null);
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  /** The click that ends a long press is not a tap: the tools it opened stay. */
  const held = useRef(false);
  useEffect(() => () => { if (press.current) clearTimeout(press.current.timer); }, []);
  const menu: MenuItem[] = [
    { id: 'copy', label: t('#ChatCopyText'), icon: <Icon name="copy" size={16} />, onSelect: () => onCopy(message) },
    ...(mine ? [
      { id: 'edit', label: t('Edit'), icon: <Icon name="pencil" size={16} />, onSelect: () => onEdit(message.id) },
      { id: 'separator', separator: true } as MenuItem,
      { id: 'delete', label: t('Delete'), icon: <Icon name="trash" size={16} />, danger: true, onSelect: () => onDelete(message.id) },
    ] : []),
  ];
  const headNames = heads?.map(user => nameIn(state, user, selfId)) ?? [];
  // A finger held on the bubble opens the picker; moving away or letting go first is a scroll or a tap.
  const holdStart = (event: ReactPointerEvent) => {
    if (event.pointerType !== 'touch' || !tools) return;
    if (press.current) clearTimeout(press.current.timer);
    press.current = { x: event.clientX, y: event.clientY, timer: setTimeout(() => {
      press.current = null; held.current = true;
      onTap(message.id, true);
      article.current?.querySelector<HTMLButtonElement>('.chat-picker .dropdown-trigger')?.click();
    }, LONG_PRESS_MS) };
  };
  const holdEnd = () => { if (press.current) { clearTimeout(press.current.timer); press.current = null; } };
  return <>
    {row.day && <p className="chat-day"><span>{dayLabel(message.sentAt)}</span></p>}
    {row.divider && <p className="chat-divider" role="separator"><span>{t('#ChatNewMessages')}</span></p>}
    <div className={cx('chat-row', mine ? 'is-mine' : 'is-theirs', grouped && 'is-grouped', groupedNext && 'has-next', reacted && 'has-reactions',
      message.local && `is-${message.local}`, highlighted && 'is-highlighted')}>
      {!mine && <span className="chat-avatar-slot">{!grouped && <ParticipantAvatar name={name} look={look} size={28} />}</span>}
      <article ref={article} className="chat-message" tabIndex={focusable ? 0 : -1} aria-labelledby={labelId} data-message-id={message.id}
        data-read-target={!mine && !message.deleted ? message.id : undefined} data-touched={touched || undefined}
        onFocus={event => { if (event.target === event.currentTarget) onFocus(message.id); }}
        onClick={event => {
          if (held.current) { held.current = false; return; }
          if (!(event.target as HTMLElement).closest('button, a')) onTap(message.id);
        }}>
        <span id={labelId} className="chat-sr">{message.deleted ? t('#ChatMessageLabelDeleted', name, timeLabel(message.sentAt)) : `${name}, ${timeLabel(message.sentAt)}`}</span>
        {!mine && !grouped && <p className="chat-author" aria-hidden="true">{name}</p>}
        <div className="chat-line">
          <div className="chat-bubble-wrap">
            {/* A double click puts ❤️ on, or takes it off, as a double tap does in Instagram. */}
            <div className="chat-bubble" style={{ '--participant-color': look.color } as CSSProperties}
              onDoubleClick={event => { if (tools && !(event.target as HTMLElement).closest('button, a')) onReact(message.id, '❤️'); }}
              onMouseDown={event => { if (event.detail > 1) event.preventDefault(); }}
              onPointerDown={holdStart} onPointerUp={holdEnd} onPointerCancel={holdEnd}
              onPointerMove={event => { if (press.current && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 8) holdEnd(); }}
              onContextMenu={event => { if (event.nativeEvent instanceof PointerEvent && event.nativeEvent.pointerType === 'touch') event.preventDefault(); }}>
              {message.replyTo && !message.deleted && <button type="button" className="chat-quote" disabled={!replied || replied.deleted}
                style={{ '--quote-color': replied ? quoteColor(replied.authorId, selfId) : '#a5a8aa' } as CSSProperties}
                aria-label={replied && !replied.deleted ? t('#ChatQuoteLabel', nameIn(state, replied.authorId, selfId), excerpt(replied.text, 60)) : t('#ChatQuoteGone')}
                onClick={() => onJump(message.replyTo!)}>
                <strong>{replied ? nameIn(state, replied.authorId, selfId) : t('#Message')}</strong>
                <span>{!replied ? t('#ChatQuoteUnavailable') : replied.deleted ? t('#ChatMessageDeleted') : excerpt(replied.text)}</span>
              </button>}
              {message.deleted ? <p className="chat-text is-deleted"><Icon name="trash" size={14} />{t('#ChatMessageDeleted')}</p>
                : <p className={cx('chat-text', emojiOnly(message.text) && 'is-emoji')}>{linkify(message.text)}
                  <span className="chat-meta">
                    {message.editedAt !== null && <span className="chat-edited">{t('#ChatEdited')}</span>}
                    <time dateTime={new Date(message.sentAt).toISOString()} title={fullTime(message.sentAt)}>{timeLabel(message.sentAt)}</time>
                    {status && <Ticks status={status} readers={readers} />}
                  </span>
                </p>}
            </div>
            {reacted && <ReactionsPill state={state} message={message} selfId={selfId} onRemove={() => onReact(message.id, null)} />}
          </div>
          {/* Instagram's three beside the bubble: react, answer, more; yours mirrored, 😊 always nearest the bubble. */}
          {tools && <div className="chat-actions" role="toolbar" aria-label={t('#ChatMessageActions')} onKeyDownCapture={event => {
            // Escape from a closed menu's button returns to the message; an open menu closes first.
            const target = event.target as HTMLElement;
            if (event.key !== 'Escape' || target.closest('[role="menu"]') || target.getAttribute('aria-expanded') === 'true') return;
            event.preventDefault(); event.stopPropagation(); article.current?.focus();
          }}>
            <ReactionPicker current={myReaction} align={mine ? 'end' : 'start'} onPick={emoji => onReact(message.id, emoji)} />
            <button type="button" className="chat-icon-button" aria-label={t('Reply')} title={t('Reply')} onClick={() => onReply(message.id)}><Icon name="reply" size={20} /></button>
            <ActionMenu label={t('#ChatMoreMessageActions')} width={210} align={mine ? 'end' : 'start'} contain=".chat-dock" triggerClassName="chat-icon-button"
              trigger={<Icon name="more" size={20} style={{ transform: 'rotate(90deg)' }} />} items={menu} />
          </div>}
        </div>
        {message.local === 'failed' && <p className="chat-failed" role="alert">
          <span>{t('#ChatNotSent')}</span>
          <button type="button" className="chat-link" onClick={() => onRetry(message.id)}>{t('#ChatRetry')}</button>
          <button type="button" className="chat-link" onClick={() => onDiscard(message.id)}>{t('Delete')}</button>
        </p>}
      </article>
      {headNames.length > 0 && <div className="chat-heads" title={t('#ChatReadBy', namesLine(headNames))} aria-hidden="true">
        {heads!.slice(0, 4).map(user => <ParticipantAvatar key={user} name={nameIn(state, user, selfId)} look={lookFor(user)} size={16} />)}
        {heads!.length > 4 && <span className="chat-heads-more">+{heads!.length - 4}</span>}
      </div>}
    </div>
  </>;
}

/** Who is typing, under the thread; the line keeps its height when nobody is. */
function TypingLine({ state, selfId }: { state: ChatState; selfId: string }) {
  const users = typingUsers(state, Date.now(), selfId);
  const names = users.map(user => nameIn(state, user, selfId));
  return <div className="chat-typing" role="status" aria-live="polite">
    {names.length > 0 && <><span className="chat-typing-dots" aria-hidden="true"><i /><i /><i /></span><span className="chat-typing-text">{typingLine(names)}</span></>}
  </div>;
}

function Composer({ session, snapshot, draft, setDraft, textarea, onSubmit, onCancelContext, onEditLast, onClose }: {
  session: ChatSession; snapshot: ChatSnapshot; draft: ChatDraft; setDraft: (update: ChatDraft | ((draft: ChatDraft) => ChatDraft)) => void;
  textarea: RefObject<HTMLTextAreaElement | null>; onSubmit: () => void; onCancelContext: () => void; onEditLast: () => boolean; onClose: () => void;
}) {
  const { state, ready, self } = snapshot;
  const editing = draft.editing ? state.messages.find(message => message.id === draft.editing) ?? null : null;
  const replied = draft.replyTo ? state.messages.find(message => message.id === draft.replyTo) ?? null : null;
  const body = draft.text.trim();
  const canSend = ready && body.length > 0 && body.length <= CHAT_TEXT_LIMIT && (!editing || body !== editing.text);
  useLayoutEffect(() => {
    const element = textarea.current;
    if (!element) return;
    element.style.height = '0px';
    element.style.height = `${Math.min(160, Math.max(24, element.scrollHeight))}px`;
  }, [draft.text, textarea]);
  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // What is typed here belongs to the message: the editor's shortcuts do not hear it.
    event.stopPropagation();
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); if (canSend) onSubmit(); }
    else if (event.key === 'Escape') { event.preventDefault(); if (draft.editing || draft.replyTo) onCancelContext(); else onClose(); }
    else if (event.key === 'ArrowUp' && !draft.text && !draft.editing && onEditLast()) event.preventDefault();
  };
  return <form className="chat-composer" onSubmit={event => { event.preventDefault(); if (canSend) onSubmit(); }}>
    {(editing || replied) && <div className="chat-context">
      <Icon name={editing ? 'pencil' : 'reply'} size={16} />
      <span className="chat-context-text">
        <strong>{editing ? t('#ChatEditingMessage') : t('#ChatReplyTo', nameIn(state, replied!.authorId, self.userId))}</strong>
        <span>{excerpt((editing ?? replied)!.text, 70)}</span>
      </span>
      <button type="button" className="chat-mini is-small" aria-label={editing ? t('#ChatCancelEdit') : t('#ChatCancelReply')} title={t('#ChatCancelEsc')} onClick={onCancelContext}><Icon name="close" size={16} /></button>
    </div>}
    {/^@\S/.test(draft.text) && <p className="chat-public-note"><Icon name="multiplayer" size={14} />{t('#WebChatAddressedPublic')}</p>}
    <div className="chat-input" onClick={event => { if (event.target === event.currentTarget) textarea.current?.focus(); }}>
      <textarea ref={textarea} rows={1} value={draft.text} maxLength={CHAT_TEXT_LIMIT} aria-label={t('#ChatComposerLabel')}
        placeholder={ready ? (editing ? t('#ChatEditPlaceholder') : t('#Message')) : t('#ChatConnecting')}
        onChange={event => { const text = event.target.value; setDraft(current => ({ ...current, text })); if (!draft.editing) session.setTyping(text.trim().length > 0); }}
        onKeyDown={keyDown} onBlur={() => session.setTyping(false)} />
      {/* A press keeps the focus in the field, and a phone's keyboard open for the next message. */}
      <button type="submit" className="chat-send" disabled={!canSend} aria-label={editing ? t('#ChatSaveChanges') : t('Send')} title={editing ? t('#ChatSaveEnter') : t('#ChatSendEnter')}
        onPointerDown={event => { if (event.button === 0) event.preventDefault(); }}>
        <Icon name={editing ? 'check' : 'sendArrow'} size={18} />
      </button>
    </div>
    {draft.text.length > CHAT_TEXT_LIMIT - 200 && <p className="chat-counter" aria-live="polite">{draft.text.length} / {CHAT_TEXT_LIMIT}</p>}
  </form>;
}
